import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {noop} from '@github-ui/noop'
import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useMemo, useRef} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderMenu$key} from './__generated__/HeaderMenu.graphql'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import styles from './HeaderMenu.module.css'

const CreateIssueButton = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  setIsIssueTitleEditActive?: (isActive: boolean) => void
}

type HeaderMenuProps = {
  headerMenuData: HeaderMenu$key
} & HeaderMenuBaseProps

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const HeaderMenuFragment = graphql`
  fragment HeaderMenu on Issue {
    viewerCanUpdateNext
    repository {
      name
      owner {
        login
      }
      isArchived
      isWritable
    }
    url
  }
`

export function HeaderMenu({
  headerMenuData,
  setIsIssueTitleEditActive,
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    useViewportQueries,
    isReadonly,
  },
}: HeaderMenuProps) {
  const {viewerCanUpdateNext, url, repository} = useFragment(HeaderMenuFragment, headerMenuData)

  const anchorRef = useRef<HTMLButtonElement>(null)
  const canEditTitle = viewerCanUpdateNext && !isReadonly

  const editIssueTitleButton = useMemo(
    () =>
      canEditTitle ? (
        <Button
          data-testid={TEST_IDS.editIssueTitleButton}
          aria-label={LABELS.editTitle}
          onClick={() => setIsIssueTitleEditActive && setIsIssueTitleEditActive(true)}
          size="medium"
        >
          {BUTTON_LABELS.editTitle}
        </Button>
      ) : null,
    [setIsIssueTitleEditActive, canEditTitle],
  )

  return (
    <div className={styles.menuActionsContainer}>
      {editIssueTitleButton}
      <div
        className={clsx(
          styles.buttonContainer,
          useViewportQueries ? styles.buttonContainer_viewport : styles.buttonContainer_container,
        )}
      >
        {showIssueCreateButton && repository.isWritable && (
          <Suspense fallback={<CreateIssueButtonShell />}>
            <CreateIssueButton
              label={BUTTON_LABELS.new}
              navigate={navigate}
              optionConfig={{
                showRepositoryPicker: false,
                issueCreateArguments: {
                  repository: {
                    owner: repository.owner.login,
                    name: repository.name,
                  },
                },
                showFullScreenButton: true,
                // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
                navigateToFullScreenOnTemplateChoice: navigate !== noop && repository !== null,
                canBypassTemplateSelection: true,
                navigate,
              }}
            />
          </Suspense>
        )}
      </div>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              ref={anchorRef}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

export function StickyHeaderMenu({
  headerMenuData,
  optionConfig: {customEditMenuEntries, additionalHeaderActions, onClose},
}: HeaderMenuProps) {
  const {url} = useFragment(HeaderMenuFragment, headerMenuData)

  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={styles.menuActionsContainer}>
      <CopyToClipboardButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {additionalHeaderActions}
      {customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              tooltipDirection="n"
              ref={anchorRef}
              className={styles.kebabMenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}
